import React, { useEffect } from "react";
import FlightDetailsCard from "./flight-details-card";
import { useSearchParams } from "next/navigation";
import Filters from "../../filter-search";
import FlightListSkeleton from "./flight-list-skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import FlightFilterNav from "./flight-filter-nav";
import SelectedFlight from "./selectedFlight";
import FlightDetailsSmallCard from "./flight-details-small-card";
import ComboFlightCard from "./combo-flight-card";
import ManualBlockMatchBadge from "./manual-block-match-badge";

import ShareFlightInfoBar from "./shareFlightInfoBar";
import { Share2 } from "lucide-react";
import CalendarFilter from "../CalendarFilter";

const FlightList = ({
  onward,
  returnFlight,
  combo,
  multiCity,
  metadata,
  routeData,
  tripType,
  manualBlockId,
  manualBlockData,
  matchedFlights = [],
  calendar = { calendarData: [], requestedDate: "" },
  readOnly = false,
  shareId,
}: {
  onward?: any;
  returnFlight?: any;
  combo?: any;
  multiCity?: any;
  metadata?: any;
  routeData?: any;
  tripType: "oneway" | "roundtrip" | "multicity";
  calendar?: {
    calendarData: {
      date: string;
      price: number;
      isJourneyDate: boolean;
    }[];
    requestedDate: string;
  };
  manualBlockId?: string;
  manualBlockData?: any;
  matchedFlights?: any[];
  readOnly?: boolean;
  shareId?: string;
}) => {
  const {
    selectedMulticityFlights,
    selectedReturnFlights,
    isLoading,
    setSelectedFlights,
    setIndexValue,
  }: any = useFlightBookingStore();

  const searchParams = useSearchParams();
  const [sortStates, setSortStates] = React.useState({
    onward: "price",
    return: "price",
    multiCity: "price",
  });
  const search: any = useSearchParams();


  const handleSortChange = (
    type: "onward" | "return" | "multiCity",
    sortBy: string
  ) => {
    setSortStates((prev) => ({
      ...prev,
      [type]: sortBy,
    }));
  };

  // Helper function to get flight identifier - must match page.tsx logic
  const getFlightId = (flight: any) => {
    // First check if flight already has an ID
    if (flight.id) {
      return flight.id;
    }
    // Try to get ID from totalPriceList first (fare ID)
    if (flight.totalPriceList?.[0]?.id) {
      return flight.totalPriceList[0].id;
    }
    // Fallback to creating ID from segment IDs
    if (flight.sI?.length > 0) {
      return flight.sI.map((segment: any) => segment.id).join("-");
    }
    // Last fallback - use flight number and route
    if (flight.sI?.[0]) {
      const firstSegment = flight.sI[0];
      return `${firstSegment.fD?.fN || "unknown"}-${firstSegment.da?.code || "unknown"
        }-${firstSegment.aa?.code || "unknown"}`;
    }
    return "unknown";
  };

  // Helper function to check if a flight is matched from manual block
  const isFlightMatched = (flight: any, tripType?: string) => {
    if (!manualBlockId || !matchedFlights.length) return false;
    const flightId = getFlightId(flight);
    const isMatched = matchedFlights.some((match: any) => {
      // Direct ID comparison since flights from page.tsx already have IDs set
      // Also check trip type if provided to ensure we're matching the right trip
      const idMatches = match.flight.id === flightId;
      const tripMatches = !tripType || match.tripType === tripType;
      return idMatches && tripMatches;
    });
    if (manualBlockId) {
      console.log(
        `Checking if flight ${flightId} (${tripType}) is matched:`,
        isMatched
      );
    }
    return isMatched;
  };

  // Helper function to get match score for a flight
  const getFlightMatchScore = (flight: any) => {
    if (!manualBlockId || !matchedFlights.length) return 0;
    const flightId = getFlightId(flight);
    const match = matchedFlights.find((match: any) => {
      // Direct ID comparison since flights from page.tsx already have IDs set
      return match.flight.id === flightId;
    });
    return match?.matchScore || 0;
  };

  // Enhanced sorting function that prioritizes matched flights when in manual block mode
  const sortFlights = (flights: any[], sortBy: string) => {
    if (!flights) return flights;

    const [sortType, direction] = sortBy.split("_");
    const multiplier = direction === "desc" ? -1 : 1;

    // Create a map of flight IDs to match data for manual block prioritization
    const matchDataMap = new Map();
    if (manualBlockId && matchedFlights.length > 0) {
      console.log("=== SORTING FLIGHTS IN LIST.TSX ===");
      console.log("Manual Block ID:", manualBlockId);
      console.log("Matched flights count:", matchedFlights.length);
      console.log("Flights to sort count:", flights.length);

      matchedFlights.forEach((match: any) => {
        if (match.flight) {
          // Use the flight ID directly from match.flight.id since it's already set in page.tsx
          const matchFlightId = match.flight.id;
          matchDataMap.set(matchFlightId, {
            matchScore: match.matchScore || 0,
            isPerfectMatch: match.isPerfectMatch || false,
            isExactMatch: match.isExactMatch || false,
          });
          console.log(`Added match data for flight ${matchFlightId}:`, {
            matchScore: match.matchScore,
            isPerfectMatch: match.isPerfectMatch,
            isExactMatch: match.isExactMatch,
          });
        } else {
          console.warn("Match found but flight is missing:", match);
        }
      });

      console.log("Match data map size:", matchDataMap.size);
      console.log(
        "Flight IDs in flights array:",
        flights.map((f) => getFlightId(f))
      );
    }

    let sortedFlights = [...flights].sort((a, b) => {
      // First priority: Manual block match prioritization (if in manual block mode)
      if (manualBlockId && matchedFlights.length > 0) {
        const aFlightId = getFlightId(a);
        const bFlightId = getFlightId(b);
        const aMatchData = matchDataMap.get(aFlightId);
        const bMatchData = matchDataMap.get(bFlightId);

        if (aMatchData || bMatchData) {
          console.log(
            `Comparing flights: ${aFlightId} (${aMatchData ? "MATCHED" : "NO MATCH"
            }) vs ${bFlightId} (${bMatchData ? "MATCHED" : "NO MATCH"})`
          );
        }

        // Perfect matches first
        if (aMatchData?.isPerfectMatch && !bMatchData?.isPerfectMatch)
          return -1;
        if (!aMatchData?.isPerfectMatch && bMatchData?.isPerfectMatch) return 1;

        // Then exact matches
        if (aMatchData?.isExactMatch && !bMatchData?.isExactMatch) return -1;
        if (!aMatchData?.isExactMatch && bMatchData?.isExactMatch) return 1;

        // Then any match vs no match
        if (aMatchData && !bMatchData) return -1;
        if (!aMatchData && bMatchData) return 1;

        // If both have matches, sort by match score
        if (aMatchData && bMatchData) {
          const scoreDiff = bMatchData.matchScore - aMatchData.matchScore;
          if (scoreDiff !== 0) return scoreDiff;
        }
      }

      // Normal sorting criteria
      switch (sortType) {
        case "duration":
          const aDuration = a.sI.reduce(
            (total: number, segment: any) =>
              total + segment.duration + (segment.cT || 0),
            0
          );
          const bDuration = b.sI.reduce(
            (total: number, segment: any) =>
              total + segment.duration + (segment.cT || 0),
            0
          );
          return (aDuration - bDuration) * multiplier;

        case "departure":
          return (
            (new Date(a.sI[0].dt).getTime() - new Date(b.sI[0].dt).getTime()) *
            multiplier
          );

        case "arrival":
          const aLastIndex = a.sI.length - 1;
          const bLastIndex = b.sI.length - 1;
          return (
            (new Date(a.sI[aLastIndex].at).getTime() -
              new Date(b.sI[bLastIndex].at).getTime()) *
            multiplier
          );

        case "price":
          return (
            (a.totalPriceList[0]?.fd?.ADULT?.fC?.TF -
              b.totalPriceList[0]?.fd?.ADULT?.fC?.TF) *
            multiplier
          );

        case "commission":
          const aCommission = a.totalPriceList[0]?.fd?.ADULT?.fC?.TAF || 0;
          const bCommission = b.totalPriceList[0]?.fd?.ADULT?.fC?.TAF || 0;
          return (bCommission - aCommission) * multiplier;

        default:
          return 0;
      }
    });

    return sortedFlights;
  };

  // Memoized sorted flights that preserve manual block sorting
  const sortedOnwardFlights = React.useMemo(() => {
    if (!onward?.length) return [];

    // In manual block mode with default price sorting, preserve the order from page.tsx
    if (manualBlockId && sortStates.onward === "price") {
      return onward;
    }

    return sortFlights(onward, sortStates.onward);
  }, [onward, sortStates.onward, manualBlockId]);

  const sortedReturnFlights = React.useMemo(() => {
    if (!returnFlight?.length) return [];

    // In manual block mode with default price sorting, preserve the order from page.tsx
    if (manualBlockId && sortStates.return === "price") {
      return returnFlight;
    }

    return sortFlights(returnFlight, sortStates.return);
  }, [returnFlight, sortStates.return, manualBlockId]);

  const sortedMultiCityFlights = React.useMemo(() => {
    if (!multiCity || !Object.keys(multiCity).length) return {};

    const sorted: any = {};
    Object.keys(multiCity).forEach((key) => {
      const flights = multiCity[key];

      // In manual block mode with default price sorting, preserve the order from page.tsx
      if (manualBlockId && sortStates.multiCity === "price") {
        console.log(
          `Preserving manual block order for multicity flights ${key}`
        );
        sorted[key] = flights;
      } else {
        sorted[key] = sortFlights(flights, sortStates.multiCity);
      }
    });

    return sorted;
  }, [multiCity, sortStates.multiCity, manualBlockId]);

  const sortedComboFlights = React.useMemo(() => {
    if (!combo?.length) return [];

    // In manual block mode with default price sorting, preserve the order from page.tsx
    if (manualBlockId && sortStates.onward === "price") {
      console.log("Preserving manual block order for combo flights");
      return combo;
    }

    return sortFlights(combo, sortStates.onward);
  }, [combo, sortStates.onward, manualBlockId]);

  // Get metadata from URL if not provided as prop
  const routeMetadata = React.useMemo(() => {
    if (metadata) return metadata;
    try {
      return JSON.parse(
        decodeURIComponent(searchParams?.get("metadata") || "[]")
      );
    } catch (error) {
      console.error("Error parsing metadata:", error);
      return [];
    }
  }, [metadata, searchParams]);

  const routeDataInfo = React.useMemo(() => {
    if (routeData) return routeData;
    try {
      return JSON.parse(decodeURIComponent(searchParams?.get("data") || "[]"));
    } catch (error) {
      console.error("Error parsing data:", error);
      return [];
    }
  }, [routeData, searchParams]);

  const getRouteInfo = (index: number) => {
    const route = routeDataInfo?.routeInfos?.[index];
    const meta = routeMetadata?.[index];
    return {
      from:
        meta?.fromCityOrAirport ||
        route?.fromCityOrAirport?.code ||
        "Unknown",
      to:
        meta?.toCityOrAirport || route?.toCityOrAirport?.code || "Unknown",
      date: route?.travelDate || "Unknown",
    };
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      };
      return date.toLocaleDateString("en-GB", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const getCheapestPrice = (flights: any[]) => {
    if (!flights?.length) return undefined;
    return Math.min(
      ...flights.map((flight) => flight.totalPriceList[0]?.fd?.ADULT?.fC?.TF)
    );
  };

  let fasterTime: string | undefined; // Global variable to store the fastest time

  const getFastestDuration = (flights: any[]) => {
    if (!flights?.length) return undefined;

    const minDuration = Math.min(
      ...flights.map((flight) => {
        return flight.sI
          ? flight.sI.reduce(
            (total: number, segment: any) =>
              total + segment.duration + (segment.cT || 0),
            0
          )
          : flight.duration; // Use top-level duration if no segments exist
      })
    );

    const hours = Math.floor(minDuration / 60);
    const minutes = minDuration % 60;

    fasterTime = `${hours}h ${minutes}m`; // Store the fastest time
    return fasterTime;
  };

  const getMaxCommission = (flights: any[]) => {
    if (!flights?.length) return undefined;
    return Math.max(
      ...flights.map((flight) => {
        const commission = flight.totalPriceList[0]?.fd?.ADULT?.fC?.TAF || 0;
        return commission;
      })
    );
  };

  // Auto-select matched flights in manual block mode
  useEffect(() => {
    if (
      manualBlockId &&
      matchedFlights.length > 0 &&
      tripType === "roundtrip"
    ) {
      // Find the best matched onward flight
      const bestOnwardMatch = matchedFlights
        .filter((match) => match.tripType === "ONWARD")
        .sort((a, b) => {
          if (a.isPerfectMatch && !b.isPerfectMatch) return -1;
          if (!a.isPerfectMatch && b.isPerfectMatch) return 1;
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          return b.matchScore - a.matchScore;
        })[0];

      // Find the best matched return flight
      const bestReturnMatch = matchedFlights
        .filter((match) => match.tripType === "RETURN")
        .sort((a, b) => {
          if (a.isPerfectMatch && !b.isPerfectMatch) return -1;
          if (!a.isPerfectMatch && b.isPerfectMatch) return 1;
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          return b.matchScore - a.matchScore;
        })[0];

      console.log("Auto-selecting matched flights:", {
        bestOnwardMatch: bestOnwardMatch?.flight?.id,
        bestReturnMatch: bestReturnMatch?.flight?.id,
        currentOnwardSelection: selectedReturnFlights?.onward?.flight?.fD?.fN,
        currentReturnSelection: selectedReturnFlights?.return?.flight?.fD?.fN,
      });

      // Use setTimeout to ensure this runs after individual card auto-selections
      setTimeout(() => {
        // Auto-select the best matched onward flight if not already selected or if current selection is not a match
        if (
          bestOnwardMatch &&
          (!selectedReturnFlights?.onward?.flight ||
            !matchedFlights.some(
              (m) =>
                m.tripType === "ONWARD" &&
                m.flight.totalPriceList[0].id ===
                selectedReturnFlights?.onward?.fare?.id
            ))
        ) {
          console.log(
            "Auto-selecting best onward match:",
            bestOnwardMatch.flight.id
          );
          setSelectedFlights("onward", {
            flight: bestOnwardMatch.flight.sI[0],
            fare: bestOnwardMatch.flight.totalPriceList[0],
          });
        }

        // Auto-select the best matched return flight if not already selected or if current selection is not a match
        if (
          bestReturnMatch &&
          (!selectedReturnFlights?.return?.flight ||
            !matchedFlights.some(
              (m) =>
                m.tripType === "RETURN" &&
                m.flight.totalPriceList[0].id ===
                selectedReturnFlights?.return?.fare?.id
            ))
        ) {
          console.log(
            "Auto-selecting best return match:",
            bestReturnMatch.flight.id
          );
          setSelectedFlights("return", {
            flight: bestReturnMatch.flight.sI[0],
            fare: bestReturnMatch.flight.totalPriceList[0],
          });
        }
      }, 100); // Small delay to ensure individual card selections are processed first
    } else if (
      sortedReturnFlights.length > 0 &&
      sortedOnwardFlights.length > 0
    ) {
      // Regular auto-selection logic for non-manual block mode
      if (
        sortedOnwardFlights[0] &&
        sortedReturnFlights[0] &&
        !selectedReturnFlights?.onward?.flight &&
        !selectedReturnFlights?.return?.flight
      ) {
        setSelectedFlights("onward", {
          flight: sortedOnwardFlights[0].sI[0],
          fare: sortedOnwardFlights[0].totalPriceList[0],
        });
        setSelectedFlights("return", {
          flight: sortedReturnFlights[0].sI[0],
          fare: sortedReturnFlights[0].totalPriceList[0],
        });
      }
    }
  }, [
    sortedOnwardFlights,
    sortedReturnFlights,
    selectedReturnFlights?.onward?.flight,
    selectedReturnFlights?.return?.flight,
    setSelectedFlights,
    manualBlockId,
    matchedFlights,
    tripType,
  ]);

  useEffect(() => {
    if (
      sortedMultiCityFlights &&
      Object.keys(sortedMultiCityFlights)?.length > 0 &&
      sortedMultiCityFlights["0"]?.length > 0
    ) {
      Object.keys(sortedMultiCityFlights)?.forEach(
        (key: string, index: number) => {
          const sortedFlights = sortedMultiCityFlights[key];

          const isAlreadySelected = selectedMulticityFlights.some(
            (data: any) => data.multiCityKey === `${index}`
          );
          if (
            sortedFlights[0] &&
            !isAlreadySelected &&
            !selectedMulticityFlights.length
          ) {
            setSelectedFlights("multicity", {
              fare: sortedFlights[0].totalPriceList[0],
              sI: sortedFlights[0].sI[0],
              multiCityKey: `${index}`,
            });
          }
        }
      );
    }
  }, [sortedMultiCityFlights, selectedMulticityFlights, setSelectedFlights]);

  const handleDateSelect = (selectedDate: Date, routeIndex: number) => {
    const formattedDate = selectedDate.toISOString().split("T")[0];
    const currentData = JSON.parse(search.get("data"));
    currentData.routeInfos[routeIndex].travelDate = formattedDate;
    const newSearchParams = new URLSearchParams(search.toString());
    newSearchParams.set("data", JSON.stringify(currentData));
    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${newSearchParams}`
    );
  };

  const NoResultsCard = () => (
    <div className="bg-white rounded-lg shadow-lg p-6 text-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          No Flights Found
        </h3>
        <p className="text-gray-600">
          No flights match your current filter criteria. Try adjusting your
          filters to see more results.
        </p>
      </div>
    </div>
  );

  return (
    <div className="bg-[#f1f5f8]">
      <div className="container mx-auto pt-4 lg:pt-10 !px-3  relative">
        <div className={`block ${readOnly ? "" : "lg:flex"} justify-between `}>
          {!readOnly && (
          <div className="lg:w-[30%] lg:pb-10 lg:sticky lg:top-20 self-start max-h-[calc(100vh-90px)] overflow-y-auto">
            <Filters />
          </div>
          )}
          <div className={`w-full pb-10 mt-4 md:mt-0 ${readOnly ? "" : "ml-2"} scrollbar-hidden overflow-x-auto`}>
            {!readOnly && (
            <div className="absolute top-5 lg:top-3 right-[15px] flex items-center">
              <span className="mr-3 flex gap-2">
                {" "}
                <Share2 className="w-4 " />{" "}
                <span className="hidden md:block">Share</span>
              </span>
              <span>
                <ShareFlightInfoBar />
              </span>
            </div>
            )}
            {tripType === "roundtrip" && !combo ? (
              <div>
                <Tabs defaultValue="oneway" orientation="vertical">
                  <TabsList className="flex w-full">
                    <TabsTrigger value="oneway" className="w-1/2">
                      <div className="flex flex-col w-full">
                        <span>
                          {getRouteInfo(0).from} → {getRouteInfo(0).to}
                        </span>
                        <span className="block text-xs md:text-sm text-gray-500">
                          {getRouteInfo(0).date &&
                            formatDate(getRouteInfo(0).date)}
                        </span>
                      </div>
                    </TabsTrigger>
                    <TabsTrigger value="return" className="w-1/2">
                      <div className="flex flex-col w-full">
                        <span>
                          {getRouteInfo(1).from} → {getRouteInfo(1).to}
                        </span>
                        <span className="block text-xs md:text-sm text-gray-500">
                          {getRouteInfo(1).date &&
                            formatDate(getRouteInfo(1).date)}
                        </span>
                      </div>
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="oneway">
                    <div className="w-full space-y-2 md:flex-1 ">
                      {/* <div className="bg-white rounded-lg py-2 px-3 md:px-4 mb-2 md:mb-4">
                        <h3 className="font-medium text-sm md:text-lg">
                          {getRouteInfo(0).from} → {getRouteInfo(0).to}
                          <span className="block text-xs md:text-sm text-gray-500">
                            {getRouteInfo(0).date &&
                              formatDate(getRouteInfo(0).date)}
                          </span>
                        </h3>
                      </div> */}

                      {!readOnly && (
                      <FlightFilterNav
                        type="onward"
                        from={getRouteInfo(0).from}
                        to={getRouteInfo(0).to}
                        date={getRouteInfo(0).date}
                        cheapestPrice={getCheapestPrice(onward)}
                        fastestDuration={getFastestDuration(onward)}
                        maxCommission={getMaxCommission(onward)}
                        onSortChange={(sortBy) =>
                          handleSortChange("onward", sortBy)
                        }
                      />
                      )}
                      {/* <CalendarFilter calendar={calendar.calendarData} routeIndex={0} onDateSelect={handleDateSelect} /> */}

                      <div className="space-y-4 px-2">
                        {sortedOnwardFlights.length > 0 ? (
                          sortedOnwardFlights.map(
                            (flight: any, index: number) => (
                              <FlightDetailsSmallCard
                                key={getFlightId(flight) || index}
                                data={flight}
                                isReturn={false}
                                routeData={routeDataInfo}
                                manualBlockId={manualBlockId}
                                manualBlockData={manualBlockData}
                                matchedFlights={matchedFlights}
                                isMatched={isFlightMatched(flight, "ONWARD")}
                                matchScore={getFlightMatchScore(flight)}
                                readOnly={readOnly}
                                shareId={shareId}
                              />
                            )
                          )
                        ) : (
                          <NoResultsCard />
                        )}
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="return">
                    <div className="w-full space-y-2 md:flex-1 scrollbar-hidden">
                      {/* <div className="bg-white rounded-lg py-2 px-3 md:px-4 mb-2 md:mb-4 ">
                        <h3 className="font-medium text-sm md:text-lg">
                          {getRouteInfo(1).from} → {getRouteInfo(1).to}
                          <span className="block text-xs md:text-sm text-gray-500">
                            {getRouteInfo(1).date &&
                              formatDate(getRouteInfo(1).date)}
                          </span>
                        </h3>
                      </div> */}
                      {!readOnly && (
                      <FlightFilterNav
                        type="return"
                        from={getRouteInfo(1).from}
                        to={getRouteInfo(1).to}
                        date={getRouteInfo(1).date}
                        cheapestPrice={getCheapestPrice(returnFlight)}
                        fastestDuration={getFastestDuration(returnFlight)}
                        maxCommission={getMaxCommission(returnFlight)}
                        onSortChange={(sortBy) =>
                          handleSortChange("return", sortBy)
                        }
                      />
                      )}
                      {/* <CalendarFilter calendar={calendar} routeIndex={1} onDateSelect={handleDateSelect} /> */}

                      <div className="space-y-4 px-2">
                        {sortedReturnFlights.length > 0 ? (
                          sortedReturnFlights.map(
                            (flight: any, index: number) => (
                              <FlightDetailsSmallCard
                                key={getFlightId(flight) || index}
                                data={flight}
                                isReturn={true}
                                routeData={routeDataInfo}
                                manualBlockId={manualBlockId}
                                manualBlockData={manualBlockData}
                                matchedFlights={matchedFlights}
                                isMatched={isFlightMatched(flight, "RETURN")}
                                matchScore={getFlightMatchScore(flight)}
                                readOnly={readOnly}
                                shareId={shareId}
                              />
                            )
                          )
                        ) : (
                          <div>
                            {/* Debug info for empty return flights */}
                            {process.env.NODE_ENV === "development" && (
                              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                                <strong>Debug Info:</strong> Return flights
                                empty. Length: {returnFlight?.length || 0},
                                Type: {typeof returnFlight}, Is Array:{" "}
                                {Array.isArray(returnFlight) ? "Yes" : "No"}
                              </div>
                            )}
                            <NoResultsCard />
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="w-full">
                {tripType === "oneway" && (
                  <div className="w-full">
                    {!readOnly && (
                    <FlightFilterNav
                      type={`${returnFlight?.length > 0 ? "return" : "onward"}`}
                      from={getRouteInfo(0).from}
                      to={getRouteInfo(0).to}
                      date={getRouteInfo(0).date}
                      cheapestPrice={getCheapestPrice(onward)}
                      fastestDuration={getFastestDuration(onward)}
                      maxCommission={getMaxCommission(onward)}
                      onSortChange={(sortBy) =>
                        handleSortChange("onward", sortBy)
                      }
                      cardSize={returnFlight?.length > 0 ? "half" : "full"}
                    />
                    )}
                    {/* <CalendarFilter calendar={calendar.calendarData} routeIndex={0} onDateSelect={handleDateSelect} /> */}

                    <div className="space-y-4 px-2">
                      {sortedOnwardFlights.length > 0 ? (
                        sortedOnwardFlights.map(
                          (flight: any, index: number) => {
                            return (
                              <div key={flight.id || index}>
                                <FlightDetailsCard
                                  key={getFlightId(flight) || index}
                                  isMultiCity={false}
                                  multiCityKey={"0"}
                                  data={flight}
                                  defaultFare={flight.totalPriceList[0]}
                                  routeDataInfo={routeDataInfo}
                                  manualBlockId={manualBlockId}
                                  manualBlockData={manualBlockData}
                                  matchedFlights={matchedFlights}
                                  isMatched={isFlightMatched(flight, "ONWARD")}
                                  matchScore={getFlightMatchScore(flight)}
                                  readOnly={readOnly}
                                  shareId={shareId}
                                />
                              </div>
                            );
                          }
                        )
                      ) : (
                        <NoResultsCard />
                      )}
                    </div>
                  </div>
                )}
                {tripType === "multicity" && !combo && (
                  <Tabs defaultValue="0" orientation="vertical">
                    <div className="flex  bg-white mb-5 p-2">
                      <TabsList className="flex flex-col sm:flex-row justify-start w-full bg-white">
                        {Object.keys(multiCity).map((key, index) => {
                          const { from, to } = getRouteInfo(parseInt(key));
                          const currentRoute =
                            routeDataInfo?.routeInfos?.[parseInt(key)];
                          return (
                            <div key={key} className="w-full sm:w-auto ">
                              <TabsTrigger
                                className="!block data-[state=active]:text-white data-[state=active]:bg-theme_color rounded-[5px] text-[13px] w-full sm:w-auto"
                                value={key}
                                onClick={() => setIndexValue(key)}
                              >
                                <h4 className="font-medium cursor-pointer">
                                  {from} - {to}
                                </h4>
                                <p className="text-[11px]">
                                  {currentRoute?.travelDate &&
                                    formatDate(currentRoute.travelDate)}
                                </p>
                              </TabsTrigger>
                            </div>
                          );
                        })}
                      </TabsList>
                    </div>

                    {Object.keys(multiCity).map((key, index) => {
                      const flights = multiCity[key];
                      const { from, to } = getRouteInfo(parseInt(key));
                      return (
                        <TabsContent
                          value={key}
                          key={key}
                          className="overflow-y-scroll scrollbar-hidden"
                        >
                          {flights?.length > 0 ? (
                            <div>
                              {!readOnly && (
                              <FlightFilterNav
                                type="onward"
                                from={from}
                                to={to}
                                date={flights[0]?.sI[0]?.dt || ""}
                                cheapestPrice={getCheapestPrice(flights)}
                                fastestDuration={getFastestDuration(flights)}
                                maxCommission={getMaxCommission(flights)}
                                onSortChange={(sortBy) =>
                                  handleSortChange("multiCity", sortBy)
                                }
                              />
                              )}
                              {/* <CalendarFilter calendar={calendar} routeIndex={index} onDateSelect={handleDateSelect} /> */}
                              <div className="space-y-4">
                                {(sortedMultiCityFlights[key] || []).map(
                                  (flight: any, flightIndex: number) => (
                                    <div key={flight.id || flightIndex}>
                                      <FlightDetailsCard
                                        key={flight.id || flightIndex}
                                        isMultiCity={true}
                                        multiCityKey={key}
                                        data={flight}
                                        defaultFare={flight.totalPriceList[0]}
                                        routeDataInfo={routeDataInfo}
                                        manualBlockId={manualBlockId}
                                        manualBlockData={manualBlockData}
                                        matchedFlights={matchedFlights}
                                        isMatched={isFlightMatched(flight, key)}
                                        matchScore={getFlightMatchScore(flight)}
                                        readOnly={readOnly}
                                        shareId={shareId}
                                      />
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          ) : (
                            <NoResultsCard />
                          )}
                        </TabsContent>
                      );
                    })}
                  </Tabs>
                )}

                {(tripType === "roundtrip" && combo) ||
                  (tripType === "multicity" && combo) ? (
                  <div className="w-full">
                    {!readOnly && (
                    <FlightFilterNav
                      type="onward"
                      from={getRouteInfo(0).from}
                      to={getRouteInfo(1).to} // Use final destination for combo
                      date={getRouteInfo(0).date}
                      cheapestPrice={getCheapestPrice(combo)}
                      fastestDuration={getFastestDuration(combo)}
                      maxCommission={getMaxCommission(combo)}
                      onSortChange={(sortBy) =>
                        handleSortChange("onward", sortBy)
                      }
                      cardSize="full"
                    />
                    )}
                    <div className="space-y-4">
                      {sortedComboFlights.length > 0 ? (
                        sortedComboFlights.map((flight: any, index: number) => (
                          <div
                            key={flight.id || index}
                            className="bg-white rounded-lg p-4 shadow"
                          >
                            <div className="flex flex-col gap-4">
                              <ComboFlightCard
                                data={flight}
                                defaultFare={flight.totalPriceList[0]}
                                isCombo={true}
                                routeDataInfo={routeDataInfo}
                                manualBlockId={manualBlockId}
                                manualBlockData={manualBlockData}
                                matchedFlights={matchedFlights}
                                isMatched={isFlightMatched(flight, "COMBO")}
                                matchScore={getFlightMatchScore(flight)}
                                readOnly={readOnly}
                                shareId={shareId}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <NoResultsCard />
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      {!readOnly &&
      (Object.keys(selectedReturnFlights?.return || []).length > 0 ||
        selectedMulticityFlights.length > 0) && (
          <SelectedFlight
            routeData={routeDataInfo}
            manualBlockId={manualBlockId}
            manualBlockData={manualBlockData}
          />
        )}
      <div className="w-full">
        {isLoading ? (
          <FlightListSkeleton count={3} isSmall={false} />
        ) : (
          <div className="flex justify-between items-center">
            {/* Existing content */}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightList;
